import { Cookie as CookieNames, FeatureFlag, Site } from '@ps/web-core-enums';
import { LoginSource } from '@ps/web-core-store';
import { format, formatISO } from 'date-fns';
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Subject, lastValueFrom } from 'rxjs';
import Cookies from 'universal-cookie';

import { Country } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useRequest from '@swf/core/hooks/useRequest';
import { registrationRequest } from '@swf/core/models/auth/register';
import { subscribe, verifyAccount } from '@swf/core/models/auth/utils';
import { fetchJWT } from '@swf/core/models/auth/verifySession';
import type { Dispatch, RootState, Store } from '@swf/core/store';
import { dispatch, select } from '@swf/core/store';
import type { BoundRequest } from '@swf/core/types/Request';
import type { WizardContext } from '@swf/core/types/Wizard';

import { getFeatureFlags } from '../../../utils/cashier';

const cookies = new Cookies();

const useSignupServices = () => {
  const usedRequest = useRequest();
  const store = useStore<RootState>() as Store;
  const site = useSelector(select.cms.getSiteId);
  const isPhone = useSelector(select.device.isPhone);
  const { account } = useDispatch<Dispatch>();
  const blacklistCheck = useFeature(FeatureFlag.BlackListCheck);

  const request: BoundRequest = useCallback(
    (options) => lastValueFrom(usedRequest(options)),
    [usedRequest]
  );

  const rmfSingleScreenCheck = useCallback(async () => {
    const features = await getFeatureFlags(store, [FeatureFlag.SwcSignupOnePage]);
    return { features };
  }, [store]);

  const setCountry = useCallback(({ values }: WizardContext) => {
    const queryParams = new URLSearchParams(document.location.search);
    const country = queryParams.get('country');
    const region = queryParams.get('state');
    if (country && region) {
      values.country = country;
      values.state = region;
    } else values.country = Country.UnitedStates;
    return Promise.resolve({});
  }, []);

  const signupCountryCheck = useCallback(
    async ({ values: { country } }: { values: { country: string } }) => {
      if (!country) return Promise.reject({});
      const installId = cookies.get(CookieNames.INSTALL_ID);
      const { body: isCountryAvailable } = await request({
        service: '/api/v1-preview/registration/country/validate',
        method: 'POST',
        http: true,
        body: { installId, country }
      });

      if (country === Country.Canada && (site === Site.CAON || site === Site.COM)) {
        const redirectionTag = isPhone && site === Site.COM ? 'caon_mob_redirect' : 'caon_signup';
        const { body: redirectUrl } = await request({
          service: 'tags.resolve',
          body: { tag: redirectionTag }
        });

        return { isCountryAvailable, redirectUrl };
      }
      return { isCountryAvailable };
    },

    [isPhone, request, site]
  );

  const signupRegister = useCallback(
    // TODO: replace with types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ({ values }: any) => {
      const loginObservable = new Subject<boolean>();
      const { dob, ...payload } = values;

      if (blacklistCheck && dob) {
        payload.dob = formatISO(values.dob);
      }

      const { body, headers } = await registrationRequest(request, payload, store.getState());
      const token =
        (await fetchJWT(
          {
            token: body.token,
            signature: body.signature,
            webId: body.userWebId
          },
          request
        )) ?? '';
      await verifyAccount(
        { token, account: body.account, loginSource: LoginSource.Registration },
        { dispatch, request },
        headers
      );

      await subscribe(dispatch, loginObservable);

      if (!blacklistCheck && values.dob) {
        await account.updateAccount({ dob: format(values.dob, 'yyyy-LL-dd'), fetchAccount: true });
      }

      const features = await getFeatureFlags(store, [
        FeatureFlag.SwcSignupRmf,
        FeatureFlag.WrmfAcAutotrigger
      ]);
      return { features, signupPassed: true };
    },
    [blacklistCheck, request, store, account]
  );

  const singleScreenSignup = useCallback(
    // TODO: replace with types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ({ values }: any) => {
      const loginObservable = new Subject<boolean>();
      const { body, headers } = await registrationRequest(request, values, store.getState());
      const token =
        (await fetchJWT(
          {
            token: body.token,
            signature: body.signature,
            webId: body.userWebId
          },
          request
        )) ?? '';
      await verifyAccount(
        { token, account: body.account, loginSource: LoginSource.Registration },
        { dispatch, request },
        headers
      );
      await subscribe(dispatch, loginObservable);
      if (values.dob) {
        await account.updateAccount({ dob: format(values.dob, 'yyyy-LL-dd'), fetchAccount: false });
      }

      const features = await getFeatureFlags(store, [
        FeatureFlag.SwcSignupRmf,
        FeatureFlag.WrmfAcAutotrigger
      ]);
      return { features, signupPassed: true };
    },
    [request, store, account]
  );
  return {
    setCountry,
    signupCountryCheck,
    signupRegister,
    rmfSingleScreenCheck,
    singleScreenSignup
  };
};

export default useSignupServices;
